/*
 * 戒导航 · 内容安全与外链管控（jiedh/content-guard）前台初始化
 * =============================================================================
 * 职责（前端体验层，对应 doc/内容安全与外链管控设计方案.md §5.6 D-4）：
 *   对「无外链权限」的用户，直接隐藏编辑器工具栏里的「插入链接」「插入图片」两个按钮，
 *   让用户在界面上就看不到入口，而不是点开、填完、提交后才收到 422 报错。
 *
 * 治理口径（与后端 JieDH\ContentGuard\Support\ExternalLinkPolicy::actorIsExempt() 完全对齐）：
 *   · 豁免人群：仅「管理员」（Administrator 组，Group::ADMINISTRATOR_ID = '1'）；
 *   · 受限人群：普通用户 + 版主组 + 其它任何自定义组。
 *   ⇒ 前端判定 = app.session.user.isAdmin()，与后端 isAdmin() 判据同源，不会出现口径漂移。
 *
 * 注意：本层只是「隐藏入口」的体验优化，不是安全边界。
 *       真正的拦截在后端第 1 层（Flarum\Post\Event\Saving → RejectExternalLinks），
 *       即使有人绕过界面（改 JS / 直接调 API）也依然会被 422 拒绝。
 *
 * 数据来源（零后端改动）：
 *   Flarum 2.x 的 isAdmin 属性随论坛 payload 下发给「当前会话用户自己」，
 *   见 vendor/flarum/core/src/Api/Resource/UserResource.php 的 Schema\Boolean::make('isAdmin')
 *   （visible 条件为 collection 是 ForumResource 且 actor 就是该 user），
 *   前端模型方法见 vendor/flarum/core/js/src/common/models/User.tsx 的 isAdmin()。
 *   ⇒ 因此无需新增论坛属性、无需改动任何 API。
 *
 * 两套编辑器必须分别处理（核心事实）：
 *   1) Markdown 模式：flarum/markdown 用 override() 整体替换了
 *      TextEditor.markdownToolbarItems()，'link'(优先级 300) 与 'image'(200) 两个按钮
 *      就是在这一层被 items.add 进去的（见 vendor/flarum/markdown/js/src/common/index.tsx）。
 *      所以拦截点是 'markdownToolbarItems'，而不是 'toolbarItems'。
 *   2) 富文本模式：fof/rich-text 的 TiptapMenu.items() 里 'link'(50) / 'image'(40)，
 *      该组件是**惰性加载模块**（webpack code splitting），因此必须用
 *      'ext:fof/rich-text/common/components/TiptapMenu' 字符串路径 + flarum.reg.onLoad 处理，
 *      直接用对象引用会拿不到模块（详见 vendor/flarum/core/js/src/common/extend.ts）。
 *
 * 顺序保证（关键）：我们用优先级 -1000 注册初始化器，保证本扩展的 extend() 一定
 *   在 flarum/markdown、fof/rich-text 的初始化器**之后**执行。
 *   原因：flarum/markdown 用的是 override()，它会「整体替换」目标方法并丢弃此前所有包装，
 *   若我们抢先执行则移除动作会被它后来的赋值冲掉（ItemList 排序规则：优先级数值越大越先执行，
 *   见 vendor/flarum/core/js/src/common/utils/ItemList.ts 的 toArray()）。
 *   这样就不依赖任何扩展加载顺序（PHP 侧按扩展标题排序），是确定性的。
 */
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';

/**
 * 当前会话用户是否拥有「插入外链 / 外链图片」的权限。
 *
 * 口径：只有管理员返回 true；普通用户、版主组、游客（未登录）一律 false。
 * 与后端 ExternalLinkPolicy::actorIsExempt() 保持一致，避免前后端口径不一致。
 */
function canInsertExternalLinks() {
  return app.session.user?.isAdmin() ?? false;
}

/**
 * 从编辑器工具栏的 ItemList 中摘掉「链接」「图片」两个入口。
 *
 * 两个编辑器（Markdown / 富文本）的键名恰好一致，都是 'link' 与 'image'，
 * 且 ItemList.remove(key) 对不存在的键是安全的（直接 delete，不抛错），
 * 所以同一个回调可以同时喂给两套编辑器的扩展点。
 */
function removeExternalLinkItems(items) {
  // 管理员：原样保留（只有管理员的编辑框里才显示这两个按钮）。
  if (canInsertExternalLinks()) return;
  items.remove('link');
  items.remove('image');
}
export default function hideExternalLinkButtons() {
  /*
   * Markdown 模式。
   * 目标方法被 flarum/markdown override 过，其内部通过 oldFunc() 取到上游 ItemList 再逐个 add，
   * 我们的回调会在它之后执行（见文件头「顺序保证」），此时 link/image 已在列表里，直接 remove 即可。
   * 泛型在这里无法从字符串路径推断，故按 Flarum 官方写法（markdown 自身源码同款）转 any。
   */
  extend('flarum/common/components/TextEditor', 'markdownToolbarItems', function (items) {
    removeExternalLinkItems(items);
  });

  /*
   * 富文本（Tiptap）模式。
   * TiptapMenu 是惰性模块：这里只是注册一个「模块加载后执行」的回调（flarum.reg.onLoad 排队），
   * 等 webpack 真正把 TiptapMenu chunk 加载进来时才把包装挂到它的原型上，
   * 而那时一定早于任何一次 items() 调用，因此不需要关心扩展加载顺序。
   */
  extend('ext:fof/rich-text/common/components/TiptapMenu', 'items', function (items) {
    removeExternalLinkItems(items);
  });
}

// 初始化器 name 必须与扩展 ID 对应（本包 ID 为 jiedh/content-guard → CLI 形式 jiedh-content-guard），
// 它同时用于 Flarum 的错误归因（app.currentInitializerExtension）。
// 第三参数 -1000 是「最后执行」的保证，理由见文件头「顺序保证」。
app.initializers.add('jiedh-content-guard', () => {
  hideExternalLinkButtons();
}, -1000);